<template>
  <div class="container">
    <h1>Statistiques</h1>

    <div class="mt-4 d-flex justify-content-between align-items-end">
      <h4 class="text-secondary">Période de {{ responseDate }}</h4>

      <div class="d-flex">
        <label class="sr-only" for="inlineFormInputName2">Client</label>
        <b-form-select
          v-model="clientToSync"
          :disabled="isSyncing"
          class="mr-2"
          :options="clientsSelectList"
          value-field="value"
          text-field="text"
          size="sm"
          placeholder="Client"
        ></b-form-select>

        <button
          type="button"
          class="ml-4 btn btn-warning position-relative"
          :disabled="isSyncing"
          @click="syncAirtable()"
        >
          <!-- Spinner when syncing -->
          <span
            v-if="isSyncing"
            class="position-absolute"
            style="
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
            "
          >
            <span
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
          </span>
          <div class="text-nowrap" :class="{ invisible: isSyncing }">
            <b-icon-arrow-repeat></b-icon-arrow-repeat> Syncroniser
            {{ selectedClient.text }} avec Airtable au {{ readableDate }}
          </div>
        </button>

        <button
          type="button"
          class="ml-2 btn btn-info position-relative"
          :disabled="isRefreshingVestaboard"
          @click="refreshVestaboard()"
          title="Mettre à jour l'affichage Vestaboard"
        >
          <span
            v-if="isRefreshingVestaboard"
            class="position-absolute"
            style="
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
            "
          >
            <span
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
          </span>
          <div class="text-nowrap" :class="{ invisible: isRefreshingVestaboard }">
            <b-icon-display></b-icon-display> Vestaboard
          </div>
        </button>
      </div>
    </div>

    <hr />

    <div class="my-2 d-flex justify-content-end align-items-end">
      <div class="d-flex">
        <form class="form-inline w-auto" @submit.prevent="refreshList">
          <label class="sr-only" for="inlineFormInputName2">Date</label>
          <input
            type="month"
            :disabled="isSyncing"
            class="form-control"
            id="date"
            v-model="date"
          />

          <button
            type="submit"
            :disabled="isSyncing"
            class="btn btn-primary ml-2"
          >
            Rechercher
          </button>
        </form>
      </div>
    </div>

    <table class="table table-striped">
      <thead>
        <tr>
          <th scope="col" class="cursor-pointer" @click="sortBy('name')">
            Client {{ sortIcon('name') }}
          </th>
          <th scope="col" class="text-center cursor-pointer" @click="sortBy('lastStatDate')">
            Dernière stat {{ sortIcon('lastStatDate') }}
          </th>
          <th scope="col" class="text-center cursor-pointer" @click="sortBy('nbSessions')">
            Sessions {{ sortIcon('nbSessions') }}
          </th>
          <th scope="col" class="text-center cursor-pointer" @click="sortBy('nbPlayers')">
            Joueurs {{ sortIcon('nbPlayers') }}
          </th>
          <th scope="col" class="text-center">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!list || list.length == 0">
          <td colspan="5" class="text-center py-4">Aucune donnée.</td>
        </tr>

        <tr v-for="(item, index) of list" :key="index">
          <td scope="row">
            <router-link :to="'/client/' + item._id">{{
              item.name
            }}</router-link>
          </td>
          <td class="text-center">{{ formatStatDate(item.lastStatDate) }}</td>
          <td class="text-center">{{ item.nbSessions }}</td>
          <td class="text-center">{{ item.nbPlayers }}</td>
          <td class="text-center">
            <button
              type="button"
              class="btn btn-sm btn-outline-warning position-relative"
              :disabled="syncingClients[item._id]"
              @click="syncClient(item)"
              title="Synchroniser ce client avec Airtable"
            >
              <!-- Spinner when syncing this specific client -->
              <span
                v-if="syncingClients[item._id]"
                class="position-absolute"
                style="
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                "
              >
                <span
                  class="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              </span>
              <span :class="{ invisible: syncingClients[item._id] }">
                <b-icon-arrow-repeat></b-icon-arrow-repeat>
              </span>
            </button>
          </td>
        </tr>

        <tr class="font-weight-bold">
          <td class="pt-4">Total</td>
          <td class="pt-4 text-center">&nbsp;</td>
          <td class="pt-4 text-center">{{ total.nbSessions }}</td>
          <td class="pt-4 text-center">{{ total.nbPlayers }}</td>
          <td class="pt-4">&nbsp;</td>
        </tr>
      </tbody>
    </table>

    <nav
      aria-label="Version navigation"
      class="pb-3 d-flex justify-content-between align-items-center"
    >
      <div>&nbsp;</div>
      <ul class="pagination cursor-pointer justify-content-center m-0">
        <li class="page-item" :class="{ disabled: page == 1 }">
          <a
            class="page-link"
            @click.prevent="page > 1 ? page-- : 1"
            tabindex="-1"
            >Précédent</a
          >
        </li>
        <li
          v-for="page_number of pages"
          :key="page_number"
          class="page-item"
          :class="{ active: page == page_number }"
        >
          <a class="page-link" @click.prevent="page = page_number">
            {{ page_number }}
          </a>
        </li>
        <li class="page-item" :class="{ disabled: page == total_pages }">
          <a
            class="page-link"
            @click.prevent="page < total_pages ? page++ : total_pages"
            >Suivant</a
          >
        </li>
      </ul>
      <div class="">{{ total_items }} éléments</div>
    </nav>
  </div>
</template>

<script>
  import axios from 'axios'
  import range from 'lodash/range'
  import slice from 'lodash/slice'
  import moment from 'moment'
  import { BIconArrowRepeat, BIconDisplay } from 'bootstrap-vue'

  export default {
    name: 'StatsPage',
    computed: {
      headers() {
        return {
          Authorization: 'Bearer ' + this.token,
        }
      },
      list() {
        let start = (this.page - 1) * this.per_page
        let end = start + this.per_page
        return slice(this.total_list, start, end)
      },
      clientsSelectList() {
        let elements = [
          {
            value: null,
            text: 'Tous',
          },
        ]

        this.total_list.forEach((item) => {
          elements.push({
            value: item._id,
            text: item.name,
          })
        })

        return elements
      },
      selectedClient() {
        return this.clientsSelectList.find(
          (item) => item.value == this.clientToSync
        )
      },
      readableDate() {
        return moment(this.date, 'YYYY-MM').format('MM-YYYY')
      },
      pages() {
        let pages = range(1, this.total_pages + 1)
        let startsAt = this.page > 5 ? this.page - 5 : 1
        let endsAt =
          this.page < this.total_pages - 5 ? this.page + 5 : this.total_pages
        if (startsAt == 1) endsAt = 10
        if (endsAt == this.total_pages) startsAt = this.total_pages - 10

        return slice(pages, startsAt - 1, endsAt - 1)
      },
      total() {
        let total = {
          nbSessions: 0,
          nbPlayers: 0,
        }

        this.total_list.forEach((item) => {
          total.nbSessions += item.nbSessions
          total.nbPlayers += item.nbPlayers
        })

        return total
      },
    },
    data() {
      return {
        token: null,

        date: this.$route.query.date || moment().format('YYYY-MM'),
        responseDate: null,

        page: 1,
        per_page: 30,
        total_list: [],
        total_items: 0,
        total_pages: 0,

        currentSort: 'name',
        currentSortDir: 'asc',

        clientToSync: null,
        isSyncing: false,
        syncingClients: {},
        isRefreshingVestaboard: false,
      }
    },
    components: {
      BIconArrowRepeat,
      BIconDisplay,
    },
    methods: {
      formatStatDate(date) {
        if (!date) return '–'
        return moment(date).format('DD/MM/YY HH:mm:ss')
      },
      sortBy(field) {
        if (this.currentSort === field) {
          this.currentSortDir = this.currentSortDir === 'asc' ? 'desc' : 'asc'
        } else {
          this.currentSort = field
          this.currentSortDir = field === 'name' ? 'asc' : 'desc'
        }
        this.total_list = this.sortList(this.total_list)
      },
      sortIcon(field) {
        if (this.currentSort !== field) return ''
        return this.currentSortDir === 'asc' ? '▲' : '▼'
      },
      sortList(list) {
        let field = this.currentSort
        let dir = this.currentSortDir === 'asc' ? 1 : -1
        return [...list].sort((a, b) => {
          let valA = a[field]
          let valB = b[field]
          if (valA == null && valB == null) return 0
          if (valA == null) return 1
          if (valB == null) return -1
          if (typeof valA === 'string') {
            return valA.localeCompare(valB) * dir
          }
          return (valA > valB ? 1 : valA < valB ? -1 : 0) * dir
        })
      },
      async refreshList() {
        try {
          let response = await axios.get('/api/stat', {
            headers: this.headers,
            params: {
              date: this.date,
            },
          })
          this.responseDate = moment(this.date, 'YYYY-MM').format('MM-YYYY')
          this.page = 1

          this.total_list = this.sortList(response.data)
          this.total_items = this.total_list.length
          this.total_pages = Math.ceil(this.total_items / this.per_page)

          if (this.$route.query.date != this.date) {
            this.$router.push({ query: { date: this.date } })
          }
        } catch (error) {
          this.$notify({
            type: 'error',
            title: 'Récupération des statistiques',
            text: error ? error : 'Erreur inconnue.',
          })
        }
      },
      async syncAirtable() {
        this.isSyncing = true
        try {
          let response = await axios.post(
            '/api/stats/airtable/sync',
            {
              date: this.date,
              client: this.clientToSync ? this.clientToSync : null,
            },
            {
              headers: this.headers,
            }
          )

          let aErrors = Object.values(response.data.errors)
          let aErrorMessages = aErrors.flatMap((item) =>
            item.map((error) => `${error.clientName}: ${error.message}`)
          )

          if (response.data.success && aErrors.length == 0) {
            this.$notify({
              type: 'success',
              title: 'Synchronisation réussie',
              text: 'Synchronisation terminée avec succès.',
            })
          } else if (!response.data.success) {
            this.$notify({
              type: 'error',
              title: 'Erreur de synchronisation',
              text: aErrorMessages.join('<br>'),
            })
          } else {
            this.$notify({
              type: 'warn',
              title: 'Synchronisation avec erreurs',
              text: aErrorMessages.join('<br>'),
            })
          }
        } catch (error) {
          this.$notify({
            type: 'error',
            title: 'Impossible de synchroniser',
            text: error ? error : 'Erreur inconnue.',
          })
        } finally {
          this.isSyncing = false
        }
      },
      async refreshVestaboard() {
        this.isRefreshingVestaboard = true
        try {
          let response = await axios.post(
            '/api/vestaboard/refresh',
            { date: this.date },
            { headers: this.headers }
          )

          if (response.data.success) {
            this.$notify({
              type: 'success',
              title: 'Vestaboard',
              text: `Affichage mis à jour (${response.data.data.totalSessions} sessions, ${response.data.data.totalPlayers} bornes)`,
            })
          } else {
            this.$notify({
              type: 'warn',
              title: 'Vestaboard',
              text: response.data.message || 'Service non activé',
            })
          }
        } catch (error) {
          this.$notify({
            type: 'error',
            title: 'Erreur Vestaboard',
            text: error?.response?.data?.message || 'Erreur inconnue',
          })
        } finally {
          this.isRefreshingVestaboard = false
        }
      },
      async syncClient(item) {
        this.$set(this.syncingClients, item._id, true)
        try {
          let response = await axios.post(
            '/api/stats/airtable/sync',
            {
              date: this.date,
              client: item._id,
            },
            {
              headers: this.headers,
            }
          )

          let aErrors = Object.values(response.data.errors)
          let aErrorMessages = aErrors.flatMap((item) =>
            item.map((error) => `${error.clientName}: ${error.message}`)
          )

          if (response.data.success && aErrors.length == 0) {
            this.$notify({
              type: 'success',
              title: 'Synchronisation réussie',
              text: 'Synchronisation terminée avec succès.',
            })
          } else if (!response.data.success) {
            this.$notify({
              type: 'error',
              title: 'Erreur de synchronisation',
              text: aErrorMessages.join('<br>'),
            })
          } else {
            this.$notify({
              type: 'warn',
              title: 'Synchronisation avec erreurs',
              text: aErrorMessages.join('<br>'),
            })
          }
        } catch (error) {
          this.$notify({
            type: 'error',
            title: 'Impossible de synchroniser',
            text: error ? error : 'Erreur inconnue.',
          })
        } finally {
          this.$set(this.syncingClients, item._id, false)
        }
      },
    },
    mounted() {
      this.token = localStorage.getItem('jwt')
      if (!this.token) {
        this.$router.push('/login')
      }

      this.refreshList()
    },
  }
</script>
